import convertHtmlEntitiesToCharacters from 'lodash/unescape';
import React, { useEffect, useState } from 'react';
import { Box, Button } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TransactionAttributes } from '@loyalty/shared-common/profile-api-client';
import { colorPalette, sxs } from '@qualifio/ui-commons';
import { faCalendar } from '@fortawesome/pro-solid-svg-icons/faCalendar';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons/faChevronLeft';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons/faChevronRight';
import { faTrophy } from '@fortawesome/pro-solid-svg-icons/faTrophy';
import { getLocalizedProperty, htmlSanitize } from '@loyalty/shared-common';
import { useTranslation } from 'react-i18next';
import { CurrencyImage } from '../../../shared/currency/CurrencyImage';
import { QlfDate } from '../../../shared/qlf-date/QlfDate';
import { SxStyles } from '../../../shared/utils/types';
import { useApi } from '../../../shared/api-client/ApiClientProvider';
import { useCurrencies } from '../../../currency/CurrenciesContext';
import { useProfiles } from '../../ProfileContext';
import { useProgram } from '../../../loyalty/ProgramContext';

const pageSize = 10;

const styles: SxStyles = {
  headerCell: {
    fontSize: '14px',
    padding: 1,
    textAlign: 'left',
    '&:first-of-type': {
      borderTopLeftRadius: 5,
    },
    '&:last-of-type': {
      borderTopRightRadius: 5,
    },
  },
  headerIcon: {
    marginRight: 0.5,
  },
  cell: {
    padding: 1,
    borderTop: '1px solid #EAEAEA',
    borderBottom: '1px solid #EAEAEA',
  },
};

export function ProfileTransactions() {
  const { t, i18n } = useTranslation();
  const { currenciesById } = useCurrencies();
  const { selectedProfile } = useProfiles();
  const { programId } = useProgram();
  const { profileApi } = useApi();
  const [currentPage, setCurrentPage] = useState(0);
  const [transactionsData, setTransactionsData] = useState({
    data: [] as TransactionAttributes[],
    isLast: true,
    pageCount: 0,
  });

  if (!selectedProfile) {
    throw new Error('No profile selected');
  }

  const fetchTransactions = async (limit: number, skip: number) => {
    const transactionsInfos = await profileApi.getTransactions(selectedProfile.id, {
      programId,
      limit,
      skip,
      orderBy: '-eventTimestamp',
    });
    setTransactionsData(transactionsInfos);
  };

  useEffect(() => {
    fetchTransactions(pageSize, 0);
    setCurrentPage(0);
  }, [selectedProfile]);

  const nextPage = async () => {
    await fetchTransactions(pageSize, (currentPage + 1) * pageSize);
    setCurrentPage(prev => prev + 1);
  };

  const previousPage = async () => {
    await fetchTransactions(pageSize, (currentPage - 1) * pageSize);
    setCurrentPage(prev => prev - 1);
  };

  if (transactionsData?.data?.length === 0) {
    return (
      <Box component="p" sx={{ color: colorPalette.grey.mainHover }}>
        {t('profiles.details.transactions.no-transaction')}
      </Box>
    );
  }

  return (
    <div>
      <Box component="table" sx={{ width: '100%', borderSpacing: 0 }}>
        <Box
          component="thead"
          sx={{
            backgroundColor: colorPalette.marine.main,
            color: colorPalette.marine.contrastText,
            fontWeight: 'bold',
            fontSize: '1.15em',
          }}
        >
          <tr>
            <Box component="th" sx={sxs(styles.headerCell, { textAlign: 'left', width: '20%' })}>
              <Box
                component={FontAwesomeIcon}
                icon={faCalendar}
                sx={styles.headerIcon}
                fixedWidth
              />
              {t('profiles.details.transactions.list.date')}
            </Box>
            <Box component="th" sx={sxs(styles.headerCell, { textAlign: 'left', width: '60%' })}>
              {t('profiles.details.transactions.list.description')}
            </Box>
            <Box component="th" sx={sxs(styles.headerCell, { textAlign: 'right', width: '20%' })}>
              <FontAwesomeIcon icon={faTrophy} fixedWidth />
            </Box>
          </tr>
        </Box>
        <tbody>
          {transactionsData.data.map(transaction => {
            const currency = currenciesById[transaction.currencyId];
            const translatableEntity = transaction.i18nAttributes
              ? { i18nAttributes: transaction.i18nAttributes }
              : { i18nAttributes: {} };
            return (
              <Box
                component="tr"
                key={transaction.id}
                sx={{ background: ({ palette }) => palette.background.paper }}
              >
                <Box
                  component="td"
                  sx={sxs(styles.cell, {
                    color: colorPalette.grey.dark,
                  })}
                >
                  <QlfDate date={transaction.eventTimestamp} />
                </Box>
                <Box
                  component="td"
                  sx={sxs(styles.cell, {
                    fontFamily: 'Open Sans, sans-serif',
                    fontSize: '14px',
                    color: colorPalette.grey.dark,
                    minWidth: 'fit-content',
                  })}
                >
                  {
                    // <
                    convertHtmlEntitiesToCharacters(
                      // &lt;
                      htmlSanitize(
                        // <p>&lt;</p>
                        getLocalizedProperty(translatableEntity, 'description', i18n.language),
                        { allowedTags: [] },
                      ),
                    )
                  }
                </Box>

                <Box
                  component="td"
                  sx={sxs(styles.cell, {
                    fontFamily: 'Bebas Neue, Open Sans, sans-serif',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    textAlign: 'right',
                    color: colorPalette.marine.main,
                    minWidth: 'fit-content',
                    width: 100,
                  })}
                >
                  {typeof transaction.amount === 'number' && currency ? (
                    <span>
                      {transaction.amount}
                      <CurrencyImage
                        currency={currency}
                        sx={{
                          display: 'inline-block',
                          verticalAlign: 'bottom',
                          marginLeft: 0.25,
                        }}
                      />
                    </span>
                  ) : (
                    ''
                  )}
                </Box>
              </Box>
            );
          })}
        </tbody>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '20px',
          alignItems: 'center',
        }}
      >
        <Button color="grey" disabled={currentPage === 0} onClick={previousPage}>
          <FontAwesomeIcon icon={faChevronLeft} />
        </Button>
        <Box
          sx={{
            fontSize: 16,
            marginX: 1,
          }}
        >{`${currentPage + 1} / ${
          transactionsData.pageCount == 0 ? 1 : transactionsData.pageCount
        }`}</Box>
        <Button color="grey" disabled={transactionsData.isLast} onClick={nextPage}>
          <FontAwesomeIcon icon={faChevronRight} />
        </Button>
      </Box>
    </div>
  );
}
